import React, { useEffect, useState, useRef } from 'react';
import { injectFonts, T } from '../utils/fonts';

const Aurora = () => {
  useEffect(() => {
    const id = 'aurora-kf';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      @keyframes aurora1{0%{transform:translate(0,0) scale(1);opacity:.55}33%{transform:translate(8%,-12%) scale(1.15);opacity:.7}66%{transform:translate(-5%,8%) scale(.95);opacity:.5}100%{transform:translate(0,0) scale(1);opacity:.55}}
      @keyframes aurora2{0%{transform:translate(0,0) scale(1);opacity:.45}33%{transform:translate(-10%,6%) scale(1.1);opacity:.6}66%{transform:translate(7%,-9%) scale(1.05);opacity:.4}100%{transform:translate(0,0) scale(1);opacity:.45}}
      @keyframes aurora3{0%{transform:translate(0,0) scale(1.05);opacity:.4}50%{transform:translate(6%,10%) scale(.9);opacity:.6}100%{transform:translate(0,0) scale(1.05);opacity:.4}}
      @keyframes shimmer{0%{background-position:-200% center}100%{background-position:200% center}}
      @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
      @keyframes cardIn{from{opacity:0;transform:translateY(32px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
      @keyframes borderSlide{0%{background-position:200% 0}100%{background-position:-200% 0}}
    `;
    document.head.appendChild(s);
  }, []);
  return (
    <div style={{position:'fixed',inset:0,overflow:'hidden',pointerEvents:'none',zIndex:0,filter:'blur(80px)'}}>
      <div style={{position:'absolute',width:'70vw',height:'70vw',maxWidth:900,maxHeight:900,borderRadius:'50%',background:'radial-gradient(circle,rgba(99,102,241,.55) 0%,rgba(139,92,246,.3) 50%,transparent 70%)',top:'-20%',left:'-10%',animation:'aurora1 14s ease-in-out infinite'}}/>
      <div style={{position:'absolute',width:'65vw',height:'65vw',maxWidth:800,maxHeight:800,borderRadius:'50%',background:'radial-gradient(circle,rgba(6,182,212,.5) 0%,rgba(16,185,129,.25) 50%,transparent 70%)',bottom:'-10%',right:'-10%',animation:'aurora2 18s ease-in-out infinite'}}/>
      <div style={{position:'absolute',width:'55vw',height:'55vw',maxWidth:700,maxHeight:700,borderRadius:'50%',background:'radial-gradient(circle,rgba(245,158,11,.35) 0%,rgba(239,68,68,.2) 50%,transparent 70%)',top:'30%',left:'35%',animation:'aurora3 22s ease-in-out infinite'}}/>
    </div>
  );
};

const SpotlightCard = ({ children, accentColor, animDelay, style }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x:'50%', y:'50%', visible:false });
  const onMove = (e) => { const r = ref.current.getBoundingClientRect(); setPos({ x:`${e.clientX-r.left}px`, y:`${e.clientY-r.top}px`, visible:true }); };
  const onLeave = () => setPos(p => ({ ...p, visible:false }));
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ position:'relative', animation:`cardIn 0.6s ${animDelay}s both cubic-bezier(0.4,0,0.2,1)`, ...style }}>
      <div style={{
        position:'absolute', inset:0, borderRadius:20,
        background:`radial-gradient(320px circle at ${pos.x} ${pos.y}, ${accentColor}28 0%, transparent 65%)`,
        opacity: pos.visible ? 1 : 0, transition:'opacity 0.35s ease',
        pointerEvents:'none', zIndex:3,
      }} />
      {children}
    </div>
  );
};

const Section = ({ title, children, last }) => (
  <section style={{ marginBottom:last?0:32, paddingBottom:last?0:32, borderBottom:last?'none':'1px solid rgba(255,255,255,0.07)' }}>
    <h2 style={{ fontSize:'clamp(1.05rem,4vw,1.5rem)', color:T.godot, fontWeight:700, margin:'0 0 14px', borderBottom:`2px solid ${T.line}`, paddingBottom:8, fontFamily: T.fontDisplay }}>{title}</h2>
    {children}
  </section>
);

const Sub = ({ children }) => (
  <h3 style={{ fontSize:'clamp(0.95rem,3.5vw,1.2rem)', color:T.text, fontWeight:600, margin:'20px 0 10px', fontFamily: T.fontDisplay }}>{children}</h3>
);

const P = ({ children }) => (
  <p style={{ fontSize:'clamp(0.88rem,3.5vw,1rem)', color:T.muted, lineHeight:1.8, marginBottom:12, fontFamily: T.fontBody }}>{children}</p>
);

const PrivacyPolicy = () => {
  useEffect(() => { injectFonts(); }, []);

  const s = {
    page: { background:T.bg, minHeight:'100vh', padding:'clamp(16px,5vw,40px)', paddingTop:'clamp(80px,14vw,120px)', fontFamily:T.fontBody, position:'relative' },
    wrap: { maxWidth:900, margin:'0 auto', position:'relative', zIndex:1 },
    card: { background:T.panel, backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:`1px solid ${T.line}`, borderRadius:20, padding:'clamp(20px,5vw,48px)', boxShadow:'0 8px 40px rgba(0,0,0,0.4)', overflow:'hidden' },
    list: { paddingRight:20, margin:'10px 0 12px', listStyle:'disc' },
    li:   { fontSize:'clamp(0.88rem,3.5vw,1rem)', color:T.muted, lineHeight:1.8, marginBottom:6, fontFamily: T.fontBody },
    contactBox: { background:'rgba(6,182,212,0.08)', border:'1px solid rgba(6,182,212,0.25)', borderRadius:12, padding:'clamp(14px,3.5vw,22px)', marginTop:12, marginBottom:12 },
    consentBox: { background:'rgba(99,102,241,0.08)', border:'1px solid rgba(99,102,241,0.25)', borderRadius:12, padding:'clamp(14px,3.5vw,22px)', marginTop:24 },
    link: { color:T.indigoLight, fontWeight:600, textDecoration:'none' },
  };

  return (
    <div style={s.page} dir="rtl">
      <Aurora />
      <div style={s.wrap}>
        <header style={{ textAlign:'center', marginBottom:40, animation:'fadeUp 0.6s 0.1s both' }}>
          <h1 style={{ fontSize:'clamp(1.8rem,7vw,3.2rem)', margin:'0 0 10px', fontWeight:700, letterSpacing:'-0.02em', fontFamily: T.fontDisplay, background:`linear-gradient(90deg,${T.text} 20%,#fff 40%,${T.muted} 60%,${T.text} 80%)`, backgroundSize:'200% auto', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text', animation:'shimmer 3.5s linear infinite' }}>
            מדיניות פרטיות
          </h1>
          <p style={{ fontSize:'clamp(0.8rem,3vw,0.9rem)', color:T.faint, fontFamily: T.fontBody }}>עדכון אחרון: {new Date().toLocaleDateString('he-IL')}</p>
        </header>

        <SpotlightCard accentColor={T.violet} animDelay={0.1}>
          <div style={s.card}>
            <div style={{
              height:3, width:'100%', marginBottom:24, borderRadius:'0 0 3px 3px',
              background:`linear-gradient(90deg, transparent 0%, ${T.violet} 30%, #3b82f6 70%, transparent 100%)`,
              backgroundSize:'200% 100%', animation:'borderSlide 4s linear infinite',
            }} />
            <Section title="1. מבוא">
              <P>GoGameDev (להלן: "אנחנו", "שלנו" או "החברה") מחויבת להגן על פרטיותכם. מדיניות פרטיות זו מסבירה כיצד אנו אוספים, משתמשים, מאחסנים ומגנים על המידע האישי שלכם בעת שימוש באתר שלנו.</P>
              <P>השימוש באתר מהווה הסכמה למדיניות פרטיות זו. אם אינכם מסכימים לתנאים המפורטים כאן, אנא הימנעו משימוש באתר.</P>
            </Section>

            <Section title="2. איזה מידע אנו אוספים">
              <Sub>2.1 מידע שאתם מספקים לנו</Sub>
              <P>כאשר אתם יוצרים קשר עמנו או נרשמים לקורסים, אנו עשויים לאסוף:</P>
              <ul style={s.list}>{['שם מלא (של ההורה/אפוטרופוס והתלמיד)','כתובת דואר אלקטרוני','מספר טלפון','גיל התלמיד','העדפות קורסים ורמת ידע','כל מידע נוסף שתבחרו לספק'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
              <Sub>2.2 מידע שנאסף באופן אוטומטי</Sub>
              <ul style={s.list}>{['כתובת IP','סוג דפדפן ומערכת הפעלה','דפים שביקרתם באתר','משך הזמן בכל דף','דפי ההפניה','תאריך ושעת הביקור'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
              <Sub>2.3 עוגיות (Cookies)</Sub>
              <P>האתר משתמש בעוגיות לשיפור חווית המשתמש, לניתוח תנועה ולמטרות שיווקיות.</P>
              <ul style={s.list}>{['עוגיות הכרחיות: נדרשות לתפקוד בסיסי','עוגיות ביצועים: מחקר אינטראקציות עם האתר','עוגיות פונקציונליות: זוכרות את הבחירות שלכם','עוגיות שיווק: מעקב לצורכי פרסום'].map((t,i)=><li key={i} style={s.li}><strong style={{color:T.text}}>{t.split(':')[0]}:</strong>{t.split(':')[1]}</li>)}</ul>
            </Section>

            <Section title="3. כיצד אנו משתמשים במידע">
              <ul style={s.list}>{['לספק ולנהל את השירותים והקורסים שלנו','לתקשר עמכם בנוגע להרשמה, לוחות זמנים ועדכונים','לשפר את חווית המשתמש באתר','לנתח דפוסי שימוש ולבצע מחקרים סטטיסטיים','לשלוח חומרי שיווק ועדכונים (רק אם נתתם הסכמה)','לעמוד בדרישות חוקיות ולהגן על זכויותינו'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
            </Section>

            <Section title="4. שיתוף מידע עם צדדים שלישיים">
              <P>אנו לא מוכרים את המידע האישי שלכם לצדדים שלישיים. עם זאת, אנו עשויים לשתף מידע עם ספקי שירותים ובמסגרת דרישות חוקיות.</P>
              <Sub>4.1 Meta (פייסבוק ואינסטגרם)</Sub>
              <P>באתר מותקן פיקסל של Meta. הוא פועל בכל ביקור באתר, גם אם לא מילאתם טופס, ומדווח ל‑Meta על:</P>
              <ul style={s.list}>{['הדפים שבהם ביקרתם ומועד הביקור','כתובת IP וסוג הדפדפן','הדף שממנו הגעתם','מזהה דפדפן (עוגיית _fbp) שנוצר על ידי Meta','מזהה הקלקה על מודעה (_fbc), אם הגעתם ממודעה'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
              <P>אם אתם מחוברים לחשבון פייסבוק או אינסטגרם, Meta עשויה לקשר מידע זה לחשבונכם.</P>
              <P>בנוסף, כאשר אתם שולחים טופס יצירת קשר, אנו מעבירים ל‑Meta פרטים על הפנייה כדי למדוד את ביצועי המודעות שלנו. הפרטים המזהים מועברים מוצפנים (SHA‑256) ולא כטקסט גלוי:</P>
              <ul style={s.list}>{['מספר הטלפון — מוצפן','השם הפרטי — מוצפן','מזהה פנייה פנימי שלנו — מוצפן','הקורס שאליו התעניינתם ומקור הפנייה'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
              <P>שימו לב: הצפנה אינה הופכת את המידע לאנונימי — היא נועדה לאפשר ל‑Meta להתאים את הפרטים לחשבון קיים אצלה. מדיניות הפרטיות של Meta חלה על השימוש שלה במידע זה.</P>
              <P>אם אינכם מעוניינים בכך, ניתן לחסום עוגיות צד שלישי בדפדפן או להשתמש בהרחבה לחסימת מעקב, וכן לפנות אלינו בבקשה למחיקת פרטיכם.</P>
            </Section>

            <Section title="5. אבטחת מידע">
              <P>אנו נוקטים באמצעי אבטחה סבירים כדי להגן על המידע האישי שלכם, כולל הצפנת מידע רגיש, שרתים מאובטחים ובדיקות אבטחה תקופתיות.</P>
            </Section>

            <Section title="6. שמירת מידע">
              <P>אנו שומרים את המידע כל עוד זה נחוץ למטרות שלשמן הוא נאסף, או כפי שנדרש על פי חוק.</P>
            </Section>

            <Section title="7. זכויותיכם">
              <ul style={s.list}>{['זכות לגישה','זכות לתיקון','זכות למחיקה','זכות להגבלת עיבוד','זכות להתנגדות','זכות לניידות','זכות למשוך הסכמה'].map((t,i)=><li key={i} style={s.li}><strong style={{color:T.text}}>{t}</strong></li>)}</ul>
            </Section>

            <Section title="8. מידע על קטינים">
              <P>השירותים שלנו מיועדים לילדים ובני נוער, אך אנו אוספים מידע רק מהורים או אפוטרופוסים חוקיים.</P>
            </Section>

            <Section title="9. קישורים לאתרים חיצוניים">
              <P>האתר עשוי לכלול קישורים לאתרים חיצוניים. אנו לא אחראים למדיניות הפרטיות של אתרים אלו.</P>
            </Section>

            <Section title="10. שינויים במדיניות">
              <P>אנו עשויים לעדכן מדיניות זו מעת לעת. כל שינוי מהותי יפורסם באתר.</P>
            </Section>

            <Section title="11. עמידה בתקנות בינלאומיות">
              <ul style={s.list}>{['חוק הגנת הפרטיות, התשמ"א-1981 (ישראל)','GDPR - General Data Protection Regulation (האיחוד האירופי)','CCPA - California Consumer Privacy Act (קליפורניה, ארה"ב)'].map((t,i)=><li key={i} style={s.li}>{t}</li>)}</ul>
            </Section>

            <Section title="12. יצירת קשר" last>
              <P>לשאלות, בקשות או תלונות בנוגע למדיניות הפרטיות:</P>
              <div style={s.contactBox}>
                <P><strong style={{color:T.text}}>GoGameDev</strong></P>
                <P><strong style={{color:T.text}}>טלפון:</strong> 054-944-7314</P>
                <P><strong style={{color:T.text}}>אימייל:</strong> GoGameDev3D@gmail.com</P>
                <P style={{margin:0}}><strong style={{color:T.text}}>WhatsApp:</strong> <a href="https://wa.me/972549447314" style={s.link}>שלח הודעה</a></P>
              </div>
              <div style={s.consentBox}>
                <P style={{margin:0}}><strong style={{color:T.godot}}>הצהרת הסכמה:</strong> השימוש באתר זה מהווה הסכמה למדיניות פרטיות זו.</P>
              </div>
            </Section>
          </div>
        </SpotlightCard>

        <footer style={{ textAlign:'center', padding:'24px 0', position:'relative', zIndex:1 }}>
          <p style={{ color:T.muted, fontSize:'0.88rem', margin:0, fontFamily: T.fontBody }}>&copy; {new Date().getFullYear()} GoGameDev - כל הזכויות שמורות</p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
